import React, { useCallback, useEffect, useReducer, useState } from "react";
import { Button, CloseButton, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

const codes = [
  "EDUCATION",
  "ELDERLY_SUPPORT",
  "ENROLLMENT",
  "SINGLE_MOTHERS",
  "CHILD_CARE",
  "RELIGIOUS_OUTREACH",
  "DISASTER_RELIEF",
  "MENTAL_HEALTH",
];

const initialState = { name: "", code: "", description: "", active: true };
const formReducer = (state, action) => {
  switch (action.type) {
    case "UPDATE_FIELD":
      return { ...state, [action.name]: action.value };
    case "TOGGLE_ACTIVE":
      return { ...state, active: !state.active };
    case "SET_FORM":
      return { ...state, ...action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
};

export const EditPurposeModal = ({
  showUpdateModal,
  setShowUpdateModal,
  selectedPurpose,
  updateDonationPurpose,
}) => {
  const [formData, dispatch] = useReducer(formReducer, initialState);
  const [submitting, setSubmitting] = useState(false);

  // populate form when a purpose is selected
  useEffect(() => {
    if (selectedPurpose) {
      dispatch({
        type: "SET_FORM",
        payload: {
          name: selectedPurpose.name || "",
          code: selectedPurpose.code || "",
          description: selectedPurpose.description || "",
          active:
            selectedPurpose.active !== undefined
              ? selectedPurpose.active
              : true,
        },
      });
    }
  }, [selectedPurpose]);

  const handleClose = useCallback(() => {
    setShowUpdateModal(false);
    dispatch({ type: "RESET" });
  }, [setShowUpdateModal]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    dispatch({ type: "UPDATE_FIELD", name, value });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.description) {
      toast.error("All fields are required");
      return;
    }

    try {
      setSubmitting(true);
      await updateDonationPurpose(selectedPurpose?.id, formData);
      handleClose();
    } catch (error) {
      console.error("Error adding new purpose:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={showUpdateModal} onHide={handleClose} keyboard={false}>
      <Modal.Header className="bg-body-tertiarydd border-0">
        <Modal.Title style={{ fontSize: 14 }} className="ps-2">
          Edit donation credit purpose
        </Modal.Title>
        <CloseButton
          className="btn btn-circle btn-sm transition-base p-0"
          onClick={handleClose}
        />
      </Modal.Header>
      <Modal.Body className="px-4 py-0 pb-3">
        <Form className="p-0" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              name="name"
              type="text"
              placeholder="e.g child care"
              required
              value={formData.name}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Code</Form.Label>
            <Form.Select
              size="sm"
              name="code"
              style={{ fontSize: "13.33px" }}
              required
              value={formData.code}
              onChange={handleChange}
            >
              <option value="">Select option</option>
              {codes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Brief description"
              name="description"
              required
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="active-switch"
              label={
                formData.active
                  ? "Active (visible in system)"
                  : "Inactive (hidden)"
              }
              checked={formData.active}
              onChange={() => dispatch({ type: "TOGGLE_ACTIVE" })}
            />
          </Form.Group>

          <div className="d-flex gap-2 mt-4">
            <Button
              size="md"
              variant="secondary"
              className="text-xs"
              onClick={() => setShowUpdateModal(false)}
            >
              Close
            </Button>
            {submitting ? (
              <Spinner animation="border" size="md" variant="success" />
            ) : (
              <Button
                size="md"
                variant="success"
                className="text-xs"
                type="submit"
              >
                Save
              </Button>
            )}
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
